import { TONE_VAR, type StatusTone } from '@/theme/statusTone';
import { cn } from '@/lib/utils';

/**
 * A small coloured dot and a text LABEL. For rows in a list.
 *
 * The label is required, not optional. A colour-only status is unreadable to a
 * colour-blind operator and invisible to `getByText`, and the second of those is how the
 * first gets caught.
 *
 * Pair with `StatusPill`, which is the filled badge for the ONE primary state of a detail
 * record. A list should never be a wall of filled badges — that is what makes the
 * genuinely important pill on a detail page stop meaning anything.
 */
export type StatusDotProps = {
  tone: StatusTone;
  label: string;
  className?: string;
  /**
   * ⚠️ [amended C1f] Optional, and additive — `AddresseeCodes`' `testId` is the precedent.
   * A screen that renders several dots needs to address ONE of them, and `data-tone` alone is
   * not a handle: it is the very thing being asserted.
   */
  'data-testid'?: string;
};

export const StatusDot = ({ tone, label, className, 'data-testid': testId }: StatusDotProps) => (
  <span
    className={cn('inline-flex items-center gap-1.5 whitespace-nowrap text-sm', className)}
    data-tone={tone}
    data-testid={testId}
  >
    <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: TONE_VAR[tone] }} aria-hidden />
    {label}
  </span>
);
